import { Component, EventEmitter, Input, Output } from "@angular/core";
import { Camera, CameraResultType, CameraSource, Photo } from "@capacitor/camera";
import { LoadingService } from "src/services/loading/loading.service";
import { MessageService } from "src/services/message/message.service";
import { UploadService } from "src/services/upload/upload.service";
import { DriverOperationsService } from "src/services/driver-operations/driver-operations.service";
import { isAndroid, isIos, isNative } from "../../../../services/utils/runtime.utils";
import { ActionSheetService } from "../../../../services/action-sheet/action-sheet.service";
import { isObjectWithProperties } from "../../../../services/utils/object.utils";

@Component({
  selector: "rc-upload-picture",
  template: `
    <ion-button (click)="pickAndUploadPicture()" color="secondary" size="small" [disabled]="disabled">
      <ion-icon name="camera-outline"></ion-icon>
    </ion-button>
  `,
  styleUrls: ["./upload-picture.component.scss"],
  standalone: false,
})
export class UploadPictureComponent {
  @Input() disabled: boolean = false;
  @Input() orderId: string | undefined;

  @Output() public onUploadCompleted = new EventEmitter<string>();
  public constructor(
    private readonly actionSheetService: ActionSheetService,
    private readonly loadingService: LoadingService,
    private readonly messageService: MessageService,
    private readonly driverOperationsService: DriverOperationsService,
    private readonly uploadService: UploadService,
  ) {}

  public async pickAndUploadPicture(): Promise<void> {
    if (isNative() || isAndroid()) {
      await this.actionSheetService.showActionSheet("Choose a source", [
        { text: "Take a new photo", handler: () => this.pickAndUploadPictureInternal(CameraSource.Camera) },
        { text: "Pick from library", handler: () => this.pickAndUploadPictureInternal(CameraSource.Photos) },
      ]);
    } else if (isIos()) {
      await this.pickAndUploadPictureInternal(CameraSource.Photos);
    } else {
      await this.pickAndUploadPictureInternal(CameraSource.Camera);
    }
  }

  private async pickAndUploadPictureInternal(source: CameraSource): Promise<void> {
    const capturedPhoto = await this.getPictureAndIgnoreCancelledError(source);

    await this.uploadPicture(capturedPhoto);
  }

  private async uploadPicture(capturedPhoto: Photo | undefined): Promise<void> {
    const base64String = capturedPhoto?.base64String;
    if (!capturedPhoto || !base64String) {
      return;
    }

    await this.loadingService.wrapInLoading(async () => {
      const url = await this.uploadService.uploadImageAndGetPublicUrl(base64String, capturedPhoto.format);

      if (this.orderId) {
        try {
          await this.driverOperationsService.uploadProofPhoto(this.orderId, url);
        } catch (error) {
          await this.messageService.presentUnexpectedErrorToast(
            "The photo was uploaded but we could not attach it to the order.",
            error,
          );
        }
      }

      this.onUploadCompleted.emit(url);
    });
  }

  private async getPictureAndIgnoreCancelledError(source: CameraSource): Promise<Photo | undefined> {
    try {
      return await Camera.getPhoto({
        resultType: CameraResultType.Base64,
        source,
        quality: 100,
      });
    } catch (error) {
      if (
        isObjectWithProperties(error, "message") &&
        typeof error.message === "string" &&
        error.message.toLowerCase().includes("cancelled")
      ) {
        return;
      }
      throw error;
    }
  }
}
